import styled, {
  AnyStyledComponent, css, StyledComponentInnerOtherProps, StyledComponentPropsWithRef,
} from 'styled-components';
import { ComponentProps } from 'react';
import { InputFocus } from './Input';
import { useFormConfigurationContext } from '../FormConfigurationProvider';

export const ButtonCss = css<{ disabled?: boolean, small?: boolean, fontSize?: string }>`${({
  theme, disabled, small, fontSize,
}) => `
    font-family: 'Open Sans', sans-serif;
    display:flex;
    flex-wrap: nowrap;
    justify-content:center;
    gap:${theme.padding[2]};
    white-space:nowrap;
    align-items:center;
    color:${disabled ? theme.colours.neutral[5] : theme.colours.neutral[10]};
    padding-top:${theme.padding[1]};
    padding-bottom:${theme.padding[1]};
    padding-left: ${small ? theme.padding[5] : theme.padding[8]};
    padding-right: ${small ? theme.padding[5] : theme.padding[8]};
    background:${disabled ? theme.colours.neutral[7] : theme.colours.primary[1]};
    border-radius: ${theme.borderRadius[2]};
    font-size: ${fontSize || (small ? theme.fontSize[1] : theme.fontSize[3])};
    font-weight: ${theme.fontWeight.bolder};
    border: none;
    min-width:${theme.padding[7]};
    cursor:  ${disabled ? 'default' : 'pointer'};
    opacity: ${disabled ? 0.5 : 1};
    line-height: ${small ? theme.fontSize[3] : theme.fontSize[6]};
    height: ${small ? theme.smallButtonHeight : theme.buttonHeight};

    &:hover {
      background: ${disabled ? '' : theme.colours.primary[2]};
    };
    &:active {
      color: ${disabled ? '' : theme.colours.primary[5]};
      background: ${disabled ? '' : theme.colours.primary[1]};
    };
  `}
  ${InputFocus}
`;

export const SharedButton = styled.button<{ disabled?: boolean, small?: boolean, fontSize?: string }>`
  ${ButtonCss};
`;

type inferStyledTypes<T extends AnyStyledComponent> =
  ComponentProps<T>
  & StyledComponentPropsWithRef<T>
  & StyledComponentInnerOtherProps<T>;

export type SharedButtonProps = inferStyledTypes<typeof SharedButton>

export function Button({ disabled, children, ...props }: SharedButtonProps) {
  const { isDisabled } = useFormConfigurationContext(disabled);
  return <SharedButton disabled={isDisabled} {...props}>{children}</SharedButton>;
}

export const PrimaryButton = styled(Button)`${({ theme, disabled }) => `
  opacity: 1;
  color:${disabled ? theme.colours.neutral[5] : theme.colours.primary[1]};
  background:${disabled ? theme.colours.neutral[7] : theme.colours.secondary[3]};
  &:hover {
    background: ${disabled ? '' : theme.colours.secondary[1]};
  };
  &:active {
    color:${disabled ? theme.colours.neutral[5] : theme.colours.primary[1]};
    background: ${disabled ? '' : theme.colours.secondary[2]};
  };
`}`;

export const SecondaryButton = styled(Button)` ${({ theme, disabled }) => `
  color: ${disabled ? theme.colours.neutral[5] : theme.colours.primary[1]};
  border: ${theme.borderWidth[1]} solid ${disabled ? theme.colours.neutral[5] : theme.colours.primary[1]};
  background:transparent;
  &:hover {
    background: ${disabled ? '' : theme.colours.primary[8]};
  };
  &:active {
    color: ${disabled ? theme.colours.neutral[5] : theme.colours.primary[1]};
    background: ${disabled ? '' : theme.colours.primary[7]};
  };
`}`;

export const InvertedButton = styled(Button)` ${({ theme, disabled }) => `
  color: ${disabled ? theme.colours.primary[2] : theme.colours.neutral[10]};
  border: ${theme.borderWidth[1]} solid ${disabled ? theme.colours.primary[2] : theme.colours.neutral[10]};
  background: transparent;
  &:hover {
    background: ${disabled ? '' : theme.colours.primary[2]};
  };
  &:active {
    color: ${disabled ? theme.colours.primary[2] : theme.colours.neutral[10]};
    background: ${disabled ? '' : theme.colours.primary[3]};
  };
`}`;

export const LinkButton = styled(Button) <{ color?: string }>`${({
  theme, color,
}) => `
  background: transparent;
  color: ${color || theme.textPrimary};
  &:hover {
    background: transparent;
  };
  &:active {
    color: ${color || theme.textPrimary};
    background: transparent;
  };
`}`;

export const IconButton = styled(Button)`
  background: transparent;
  padding: unset;
  min-width: unset;
  height: fit-content;
  color: unset;

  &:hover {
    background: transparent;
  };

  &:active {
    color: unset;
    background: transparent;
  };
`;

export const MSButton = styled(Button)` ${({ theme, disabled }) => `
   background: ${disabled ? theme.colours.neutral[7] : theme.colours.neutral[10]};;
   color:${disabled ? theme.colours.neutral[5] : theme.colours.neutral[3]};
   gap:${theme.padding[2]};
   border:1px solid ${disabled ? theme.colours.neutral[5] : theme.colours.neutral[3]};
   border-radius:${theme.borderRadius[2]};
   &:hover {
    background: ${theme.colours.neutral[8]};
  };
  &:active {
    background: ${theme.colours.neutral[7]};
    color: ${theme.colours.neutral[3]};
  };
`}`;

export const SubmitButton = styled.button<{ disabled?: boolean }>` ${({ theme, disabled }) => `
    width: 100%;
    height: 100%;
    min-width: ${theme.buttonSubmitWidth};
    padding: ${theme.padding[3]} ${theme.padding[5]};
    font-family: 'Open Sans';
    border-radius: ${theme.borderRadius[2]};
    background-color: ${disabled ? theme.colours.neutral[7] : theme.colours.secondary[3]};
    color: ${disabled ? theme.colours.neutral[5] : theme.colours.primary[1]};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: ${theme.padding[4]};
    align-items: center;
    cursor: ${disabled ? 'default' : 'pointer'};
    border: none;
    ${!disabled && `&:hover {
      background-color: ${theme.colours.secondary[1]};
      box-shadow: 0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06);
    }`}
    &:active {
      background:${theme.colours.secondary[2]};
      box-shadow: 0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06);
    }
  `}
  ${InputFocus}
`;

export const SubmitButtonTitle = styled.p`
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize[1]};
  font-weight: ${({ theme }) => theme.fontWeight.bolder};
  line-height: ${({ theme }) => theme.fontSize[3]};
`;

export const PrimaryInvertedButton = styled(Button) <{ disabled?: boolean, loading?: boolean }>` ${({ theme, disabled, loading }) => `
  color: ${theme.colours.primary[1]};
  border: none;
  background-color: ${theme.colours.neutral[10]};
  &:hover {
    background-color: ${theme.colours.primary[5]};
  };
  &:active {
    background-color: ${theme.colours.primary[3]};
    color: ${theme.colours.primary[1]};
  };
  ${disabled && `{
    background-color: ${theme.colours.primary[2]} !important;
  }`};
  ${loading && `{
    background-color: ${theme.colours.neutral[10]} !important;
  }`};
`}`;
